import React, { useRef } from 'react';
import { Clock } from 'lucide-react';
import { theme } from '../../lib/theme';
import { formatTime } from '../../lib/timeUtils';

interface TimeInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
  preferences?: any;
}

export const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onChange,
  label,
  className = '',
  disabled = false,
  preferences
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    // showPicker() forces the native dropdown to open on Desktop where simply clicking the input area might only focus it.
    const inputEl = e.currentTarget;
    if (inputEl && 'showPicker' in HTMLInputElement.prototype) {
        try {
            inputEl.showPicker();
        } catch (error) {
            console.warn('showPicker failed', error);
        }
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className={theme.typography.sectionLabel}>
          {label}
        </label>
      )}
      <div className="relative group">
        {/* Styled display element (BEHIND the input) */}
        <div
          tabIndex={-1}
          aria-hidden="true"
          className={`w-full px-4 py-3 border border-gray-200 dark:border-white/10 rounded-xl bg-white dark:bg-slate-900/40 flex items-center justify-between transition-all outline-none ${disabled ? 'opacity-60 cursor-not-allowed' : 'group-hover:border-blue-500/50 dark:group-hover:border-blue-400/50 group-active:bg-gray-50 dark:group-active:bg-white/5'}`}
        >
          <span className={`font-bold transition-colors ${!value ? 'text-gray-300 dark:text-white/20' : 'text-gray-900 dark:text-white'}`}>
            {value ? formatTime(value, preferences) : 'Izvēlieties laiku'}
          </span>
          <Clock size={18} className={`${disabled ? 'text-gray-300' : 'text-gray-400 group-hover:text-blue-500'} transition-colors shrink-0`} />
        </div>

        {/* Native input (ON TOP but invisible) */}
        {!disabled && (
          <input
            ref={inputRef}
            type="time"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onClick={handleInputClick}
            className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-20"
            style={{ fontSize: '16px' }} // Prevents iOS zoom
          />
        )}
      </div>
    </div>
  );
};
